import { Anchor, Badge, Group, Paper, Stack, Table, Text, UnstyledButton } from "@mantine/core";
import { Fragment, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { summiereBudget, type WeeklyBillingKind, type WeeklyBudget, type WeeklyBudgetRow } from "@msc/shared";

import { formatIsoDate } from "../../lib/date.js";
import { budgetBloecke, budgetSummenHinweis, formatEuro, formatProzent } from "../../lib/weekly.js";
import { Datenstand } from "./Datenstand.js";

/**
 * Budgetübersicht der Serviceprojekte der eigenen Beauftragungen — read-only.
 * Seit #333 in Blöcken je Abrechnungsart mit eigenem Fokus: Kontingente das
 * Restbudget, Pauschalen der Auftragszeitraum. Unterprojekte stehen eingeklappt
 * unter ihrem Hauptprojekt, das den ganzen Topf zeigt (#307). Snapshots von vor
 * #333 kennen keine Abrechnungsart und erscheinen weiter als eine Tabelle.
 */
export function BudgetSection({
  budget,
  sfSyncedAt,
  salesforceRecordBaseUrl,
  bezugszeit,
}: {
  budget: WeeklyBudget;
  sfSyncedAt: string | null;
  salesforceRecordBaseUrl: string | null;
  bezugszeit: Date;
}) {
  const { t } = useTranslation();
  const summenHinweis = budgetSummenHinweis(t, budget.rows);
  const bloecke = budgetBloecke(budget.rows);

  return (
    <Paper withBorder p="md">
      <Stack gap="xs">
        <Text fw={600}>{t("weekly.metrics.budgetTitle")}</Text>
        <Datenstand labelKey="weekly.metrics.sfAsOf" syncedAt={sfSyncedAt} bezugszeit={bezugszeit} />
        {budget.rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("weekly.metrics.noProjects")}
          </Text>
        ) : bloecke === null ? (
          <BudgetTabelle art="sonstige" rows={budget.rows} salesforceRecordBaseUrl={salesforceRecordBaseUrl} />
        ) : (
          bloecke.map(({ art, rows }) => (
            <Stack key={art} gap={4}>
              <Text fw={600} size="sm">
                {t(`weekly.metrics.kind.${art}`)}
              </Text>
              <Text size="xs" c="dimmed">
                {t(`weekly.metrics.kindHint.${art}`)}
              </Text>
              <BudgetTabelle art={art} rows={rows} salesforceRecordBaseUrl={salesforceRecordBaseUrl} />
            </Stack>
          ))
        )}
        {summenHinweis && (
          <Text size="sm" c="dimmed">
            {summenHinweis}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function BudgetTabelle({
  art,
  rows,
  salesforceRecordBaseUrl,
}: {
  art: WeeklyBillingKind;
  rows: WeeklyBudgetRow[];
  salesforceRecordBaseUrl: string | null;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const kinder = new Map<string, WeeklyBudgetRow[]>();
  for (const r of rows) {
    if (!r.parentSalesforceProjectId) continue;
    kinder.set(r.parentSalesforceProjectId, [...(kinder.get(r.parentSalesforceProjectId) ?? []), r]);
  }
  const oben = rows.filter((r) => !r.parentSalesforceProjectId || !rows.some((o) => o.salesforceProjectId === r.parentSalesforceProjectId));
  const umschalten = (id: string) =>
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  const summe = summiereBudget(rows);

  return (
    <Table.ScrollContainer minWidth={art === "pauschale" ? 760 : 900}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("weekly.metrics.customer")}</Table.Th>
            <Table.Th>{t("weekly.metrics.project")}</Table.Th>
            <Table.Th>{t("weekly.metrics.projectName")}</Table.Th>
            {art !== "kontingent" && <Table.Th>{t("weekly.metrics.start")}</Table.Th>}
            <Table.Th>{t("weekly.metrics.end")}</Table.Th>
            {art === "pauschale" ? (
              <>
                <Table.Th ta="right">{t("weekly.metrics.daysUntilEnd")}</Table.Th>
                <Table.Th ta="right">{t("weekly.metrics.budget")}</Table.Th>
              </>
            ) : (
              <>
                <Table.Th ta="right">{t("weekly.metrics.budget")}</Table.Th>
                <Table.Th ta="right">{t("weekly.metrics.expectedRevenue")}</Table.Th>
                <Table.Th ta="right">{t("weekly.metrics.budgetLeft")}</Table.Th>
                <Table.Th ta="right">{t("weekly.metrics.budgetLeftPercent")}</Table.Th>
              </>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {oben.map((r) => {
            const unter = kinder.get(r.salesforceProjectId) ?? [];
            const aufgeklappt = offen.has(r.salesforceProjectId);
            return (
              <Fragment key={r.salesforceProjectId}>
                <BudgetZeile
                  art={art}
                  row={r}
                  salesforceRecordBaseUrl={salesforceRecordBaseUrl}
                  umschalter={
                    unter.length > 0 ? (
                      <UnstyledButton
                        onClick={() => umschalten(r.salesforceProjectId)}
                        aria-expanded={aufgeklappt}
                        aria-label={t("weekly.metrics.toggleSubprojects", {
                          project: r.projectNumber,
                          count: unter.length,
                        })}
                      >
                        <Text size="xs" c="blue">
                          {`${aufgeklappt ? "▾" : "▸"} ${t("weekly.metrics.subprojects", { count: unter.length })}`}
                        </Text>
                      </UnstyledButton>
                    ) : null
                  }
                />
                {aufgeklappt &&
                  unter.map((u) => (
                    <BudgetZeile
                      key={u.salesforceProjectId}
                      art={art}
                      row={u}
                      salesforceRecordBaseUrl={salesforceRecordBaseUrl}
                      unterprojekt
                    />
                  ))}
              </Fragment>
            );
          })}
        </Table.Tbody>
        {art !== "pauschale" && (
          <Table.Tfoot>
            <Table.Tr fw={700}>
              <Table.Td colSpan={art === "kontingent" ? 4 : 5}>{t("weekly.metrics.total")}</Table.Td>
              <Table.Td ta="right">{formatEuro(summe.budgetEur)}</Table.Td>
              <Table.Td ta="right">{formatEuro(summe.expectedRevenueEur)}</Table.Td>
              <Table.Td ta="right">{formatEuro(summe.budgetLeftEur)}</Table.Td>
              <Table.Td ta="right">{formatProzent(summe.budgetLeftPercent)}</Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        )}
      </Table>
    </Table.ScrollContainer>
  );
}

function BudgetZeile({
  art,
  row,
  salesforceRecordBaseUrl,
  umschalter = null,
  unterprojekt = false,
}: {
  art: WeeklyBillingKind;
  row: WeeklyBudgetRow;
  salesforceRecordBaseUrl: string | null;
  umschalter?: ReactNode;
  unterprojekt?: boolean;
}) {
  const { t } = useTranslation();
  const hervorheben = !unterprojekt && (row.lowBudget || row.endingSoon);
  // Am Hauptprojekt mit Unterprojekten zählt der ganze Topf (#307).
  const budgetEur = row.pot ? row.pot.budgetEur : row.budgetEur;
  const budgetLeftEur = row.pot ? row.pot.budgetLeftEur : row.budgetLeftEur;
  const expectedRevenueEur = row.pot ? row.pot.expectedRevenueEur : row.expectedRevenueEur;
  const ohneBudget = budgetEur === null;
  const badges = (
    <>
      {row.lowBudget && art !== "pauschale" && !unterprojekt && (
        <Badge size="xs" color="red" variant="light">
          {t("weekly.metrics.lowBudget")}
        </Badge>
      )}
      {row.endingSoon && !unterprojekt && (
        <Badge size="xs" color="orange" variant="light">
          {t("weekly.metrics.endingSoon")}
        </Badge>
      )}
    </>
  );

  return (
    <Table.Tr bg={hervorheben ? "var(--mantine-color-yellow-light)" : undefined} c={unterprojekt ? "dimmed" : undefined}>
      <Table.Td>{unterprojekt ? "" : row.customerName}</Table.Td>
      <Table.Td style={{ whiteSpace: "nowrap", paddingLeft: unterprojekt ? 24 : undefined }}>
        {salesforceRecordBaseUrl ? (
          <Anchor
            href={`${salesforceRecordBaseUrl}/${row.salesforceProjectId}`}
            target="_blank"
            rel="noreferrer"
            size="sm"
          >
            {row.projectNumber}
          </Anchor>
        ) : (
          row.projectNumber
        )}
      </Table.Td>
      <Table.Td>
        <Stack gap={0}>
          {row.title}
          {umschalter}
        </Stack>
      </Table.Td>
      {art !== "kontingent" && <Table.Td>{formatIsoDate(row.startDate)}</Table.Td>}
      <Table.Td>{formatIsoDate(row.endDate)}</Table.Td>
      {art === "pauschale" ? (
        <>
          <Table.Td ta="right">
            <Group gap={4} justify="flex-end" wrap="nowrap">
              {row.daysUntilEnd ?? "–"}
              {badges}
            </Group>
          </Table.Td>
          <Table.Td ta="right" c="dimmed">
            {ohneBudget ? t("weekly.metrics.noBudget") : formatEuro(budgetEur)}
          </Table.Td>
        </>
      ) : (
        <>
          {ohneBudget ? (
            <Table.Td ta="right" c="dimmed">
              {t("weekly.metrics.noBudget")}
            </Table.Td>
          ) : (
            <Table.Td ta="right">{formatEuro(budgetEur)}</Table.Td>
          )}
          <Table.Td ta="right">{formatEuro(expectedRevenueEur)}</Table.Td>
          <Table.Td ta="right">{ohneBudget ? "–" : formatEuro(budgetLeftEur)}</Table.Td>
          <Table.Td ta="right">
            <Group gap={4} justify="flex-end" wrap="nowrap">
              {ohneBudget || unterprojekt ? "–" : formatProzent(row.budgetLeftPercent)}
              {badges}
            </Group>
          </Table.Td>
        </>
      )}
    </Table.Tr>
  );
}
